import React, { useEffect, useId, useRef, useState } from "react";
import "../styles/EtikafSelect.css";

const EtikafSelect = ({
  id,
  label,
  value,
  options = [],
  onChange,
  disabled = false,
  renderLeading,
}) => {
  const selectedIndex = Math.max(0, options.findIndex((option) => option.value === value));
  const selected = options[selectedIndex] || options[0];
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const rootRef = useRef(null);
  const reactId = useId();
  const listboxId = `${id || "etikaf-select"}-${reactId.replace(/:/g, "")}-options`;

  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  useEffect(() => setActiveIndex(selectedIndex), [selectedIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  const close = ({ restoreFocus = true } = {}) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  const choose = (index) => {
    const option = options[index];
    if (option && !option.disabled) onChange?.(option.value);
    close();
  };

  const openFromTrigger = (event) => {
    if (disabled || options.length === 0) return;
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      setActiveIndex(selectedIndex);
      setOpen(true);
    }
  };

  const handleListKey = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      let next = activeIndex;
      do {
        next = (next + direction + options.length) % options.length;
      } while (options[next]?.disabled && next !== activeIndex);
      setActiveIndex(next);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      choose(activeIndex);
    } else if (event.key === "Escape" || event.key === "Tab") {
      if (event.key === "Escape") event.preventDefault();
      close({ restoreFocus: event.key === "Escape" });
    }
  };

  if (!selected) return null;

  return (
    <div className={`etikaf-select${renderLeading ? " etikaf-select-with-leading" : ""}`} ref={rootRef}>
      <button
        id={id}
        ref={triggerRef}
        className="etikaf-select-trigger"
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        data-has-leading={renderLeading ? "true" : "false"}
        disabled={disabled}
        onClick={() => {
          if (!disabled && options.length > 0) setOpen((current) => !current);
        }}
        onKeyDown={openFromTrigger}
      >
        {renderLeading && (
          <span className="etikaf-select-leading">{renderLeading(selected)}</span>
        )}
        <span className="etikaf-select-value">{selected.label}</span>
        <svg className="etikaf-select-chevron" viewBox="0 0 20 20" aria-hidden="true">
          <path d="m5.5 7.5 4.5 4.5 4.5-4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          ref={listRef}
          id={listboxId}
          className="etikaf-select-options"
          role="listbox"
          aria-label={`Pilihan ${label.toLowerCase()}`}
          aria-activedescendant={`${listboxId}-${options[activeIndex].value}`}
          tabIndex="-1"
          onKeyDown={handleListKey}
        >
          {options.map((option, index) => (
            <button
              id={`${listboxId}-${option.value}`}
              className="etikaf-select-option"
              type="button"
              role="option"
              aria-selected={option.value === value}
              data-active={index === activeIndex ? "true" : "false"}
              disabled={option.disabled}
              key={option.value}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => choose(index)}
            >
              {renderLeading && (
                <span className="etikaf-select-leading">{renderLeading(option)}</span>
              )}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EtikafSelect;
